// frontend/src/pages/reports/SalesReport.js
import React, { useState, useEffect } from 'react';
import { api, channelLabel } from '../../App';

const CHANNELS = ['all','ebay_source','ebay_store2','walmart','amazon','poshmark'];
const PERIODS   = [
  { label: '7 Days',   days: 7  },
  { label: '30 Days',  days: 30 },
  { label: '90 Days',  days: 90 },
  { label: 'All Time', days: 9999 },
];

const CH_COLORS = {
  ebay_source:  '#6c63ff',
  ebay_store2:  '#a78bfa',
  walmart:      '#fbbf24',
  amazon:       '#ff9900',
  poshmark:     '#f472b6',
  poshmark_old: '#f472b6',
};

function fmt$(n) { return '$' + (n||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,','); }
function fmtN(n) { return (n||0).toLocaleString(); }

export default function SalesReport() {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState(30);
  const [channel, setChannel] = useState('all');

  useEffect(() => {
    setLoading(true);
    api.get('/orders?status=shipped&limit=2000').then(r => {
      setOrders(r.data.orders || []);
      setLoading(false);
    });
  }, []);

  // Filter by period and channel
  const cutoff   = period === 9999 ? null : new Date(Date.now() - period * 86400000);
  const filtered = orders.filter(o => {
    const date = new Date(o.created_at);
    if (cutoff && date < cutoff) return false;
    if (channel !== 'all' && o.channel !== channel) return false;
    return true;
  });

  // Stats
  const totalRevenue = filtered.reduce((s,o) => s + (o.sale_price||0), 0);
  const totalOrders  = filtered.length;
  const avgOrder     = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  // By channel
  const byChannel = {};
  filtered.forEach(o => {
    const ch = o.channel;
    if (!byChannel[ch]) byChannel[ch] = { orders: 0, revenue: 0 };
    byChannel[ch].orders++;
    byChannel[ch].revenue += (o.sale_price||0);
  });

  // By day (last 30 days max)
  const dayMap = {};
  filtered.forEach(o => {
    const d = o.created_at?.substring(0,10);
    if (!d) return;
    if (!dayMap[d]) dayMap[d] = { revenue: 0, orders: 0 };
    dayMap[d].revenue += (o.sale_price||0);
    dayMap[d].orders++;
  });
  const days = Object.entries(dayMap).sort(([a],[b]) => a.localeCompare(b)).slice(-30);
  const maxRev = Math.max(...days.map(([,v]) => v.revenue), 1);

  // Top SKUs
  const skuMap = {};
  filtered.forEach(o => {
    if (!o.sku) return;
    if (!skuMap[o.sku]) skuMap[o.sku] = { sku: o.sku, title: o.title, orders: 0, revenue: 0 };
    skuMap[o.sku].orders++;
    skuMap[o.sku].revenue += (o.sale_price||0);
  });
  const topSKUs = Object.values(skuMap).sort((a,b) => b.revenue - a.revenue).slice(0,10);

  // By bin
  const binMap = {};
  filtered.forEach(o => {
    const bin = o.location || o.sku_bin || '?';
    if (!binMap[bin]) binMap[bin] = { bin, orders: 0, revenue: 0 };
    binMap[bin].orders++;
    binMap[bin].revenue += (o.sale_price||0);
  });
  const topBins = Object.values(binMap).sort((a,b) => b.revenue - a.revenue).slice(0,10);

  if (loading) return <div style={{ color:'#666', padding:40, textAlign:'center' }}>Loading sales data…</div>;

  return (
    <div>
      {/* Controls */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:24 }}>
        <div style={{ display:'flex', gap:4 }}>
          {PERIODS.map(p => (
            <button key={p.days} onClick={() => setPeriod(p.days)}
              className={`filter-tab ${period === p.days ? 'active' : ''}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:4 }}>
          {CHANNELS.map(c => (
            <button key={c} onClick={() => setChannel(c)}
              className={`filter-tab ${channel === c ? 'active' : ''}`}>
              {c === 'all' ? 'All' : channelLabel(c)}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:28 }}>
        {[
          { label:'Total Revenue', value: fmt$(totalRevenue), color:'#6c63ff' },
          { label:'Orders Shipped', value: fmtN(totalOrders), color:'#4ade80' },
          { label:'Avg Order Value', value: fmt$(avgOrder), color:'#fbbf24' },
        ].map(k => (
          <div key={k.label} className="card" style={{ textAlign:'center', padding:'20px 16px' }}>
            <div style={{ fontSize:28, fontWeight:900, color:k.color }}>{k.value}</div>
            <div style={{ fontSize:12, color:'#666', marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Revenue Chart */}
      {days.length > 0 && (
        <div className="card" style={{ marginBottom:28 }}>
          <div className="card-title">Daily Revenue</div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:3, height:120, paddingTop:10 }}>
            {days.map(([date, val]) => (
              <div key={date} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}
                title={`${date}: ${fmt$(val.revenue)} (${val.orders} orders)`}>
                <div style={{ width:'100%', background:'#6c63ff', borderRadius:'3px 3px 0 0', opacity:.85,
                  height: `${(val.revenue/maxRev)*100}%`, minHeight:2,
                  transition:'height .3s' }} />
              </div>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, fontSize:10, color:'#444' }}>
            <span>{days[0]?.[0]}</span>
            <span>{days[days.length-1]?.[0]}</span>
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:28 }}>
        {/* By Channel */}
        <div className="card">
          <div className="card-title">By Channel</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #2a2d3e' }}>
                {['Channel','Orders','Revenue','%'].map(h => (
                  <th key={h} style={{ padding:'8px 10px', fontSize:11, color:'#666', textAlign: h==='Channel'?'left':'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(byChannel).sort(([,a],[,b]) => b.revenue-a.revenue).map(([ch, v]) => (
                <tr key={ch} style={{ borderBottom:'1px solid #1a1d2e' }}>
                  <td style={{ padding:'8px 10px', fontSize:13 }}>
                    <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%',
                      background: CH_COLORS[ch]||'#555', marginRight:8 }} />
                    {channelLabel(ch)}
                  </td>
                  <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right' }}>{v.orders}</td>
                  <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right', color:'#4ade80' }}>{fmt$(v.revenue)}</td>
                  <td style={{ padding:'8px 10px', fontSize:11, textAlign:'right', color:'#666' }}>
                    {((v.revenue/totalRevenue)*100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top Bins */}
        <div className="card">
          <div className="card-title">Top Bins by Revenue</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #2a2d3e' }}>
                {['Bin','Orders','Revenue'].map(h => (
                  <th key={h} style={{ padding:'8px 10px', fontSize:11, color:'#666', textAlign: h==='Bin'?'left':'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topBins.map(b => (
                <tr key={b.bin} style={{ borderBottom:'1px solid #1a1d2e' }}>
                  <td style={{ padding:'8px 10px' }}>
                    <span style={{ background:'rgba(108,99,255,.2)', color:'#a89fff', padding:'2px 8px',
                      borderRadius:6, fontSize:12, fontFamily:'monospace', fontWeight:700 }}>{b.bin}</span>
                  </td>
                  <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right' }}>{b.orders}</td>
                  <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right', color:'#4ade80' }}>{fmt$(b.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top SKUs */}
      <div className="card">
        <div className="card-title">Top 10 SKUs by Revenue</div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:'1px solid #2a2d3e' }}>
              {['SKU','Title','Orders','Revenue'].map(h => (
                <th key={h} style={{ padding:'8px 10px', fontSize:11, color:'#666', textAlign: h==='SKU'||h==='Title'?'left':'right' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {topSKUs.map((s,i) => (
              <tr key={s.sku} style={{ borderBottom:'1px solid #1a1d2e' }}>
                <td style={{ padding:'8px 10px', fontSize:12, fontFamily:'monospace', color:'#6c63ff' }}>
                  #{i+1} {s.sku}
                </td>
                <td style={{ padding:'8px 10px', fontSize:12, color:'#aaa', maxWidth:300,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {s.title || '—'}
                </td>
                <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right' }}>{s.orders}</td>
                <td style={{ padding:'8px 10px', fontSize:13, textAlign:'right', color:'#4ade80', fontWeight:700 }}>
                  {fmt$(s.revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
